import PriceV2 from "../../common/PriceV2";
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import useDidMountEffect from "../../utils/useDidMountEffect";
import { calculatePercentage, toFixedTrunc } from "../../utils/helper";
import { useToasts } from "react-toast-notifications";
import { discountPrice, linkNameLength } from "../../utils/rules";
import currency from "currency.js";
import { appUrl } from "../../utils/constants";
import copy from "copy-to-clipboard";
import moment from 'moment';
import AlertConfirmModal from "../../common/AlertConfirmModal";
import pusher from "../../utils/pusher";

const DiscountLink = ({
    user,
    userId
}) => {
    const { addToast } = useToasts();
    const [isLoading, setIsLoading] = useState(false);
    const [percentShow, setPercentShow] = useState(0);
    const [amount, setAmount] = useState(0);
    const [enabled, setEnabled] = useState(false);
    const [linkName, setLinkName] = useState('');
    const minSubscriptionPrice = 2.99;


    const [isGenerating, setIsGenerating] = useState(false);
    const [discountUrl, setDiscountUrl] = useState('');
    const [discountCode, setDiscountCode] = useState('');
    const [linkCreatedAt, setLinkCreatedAt] = useState('-');
    const [linkClaimsCount, setLinkClaimsCount] = useState('-');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState('');

    const [puChannelName, setPuChannelName] = useState('');
    const [puChannel, setPuChannel] = useState(null);

    const randomString = (length) => {
        let charSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let randomString = '';
        for (var i = 0;i < length;i++) {
            let randomPoz = Math.floor(Math.random() * charSet.length);
            randomString += charSet.substring(randomPoz, randomPoz + 1);
        }

        return randomString;
    };

    const generateNewLink = () => {
        const code = randomString(24);

        setIsGenerating(true);

        if (user.link_url === code) {
            setIsGenerating(false);
            addToast('Please create new link', { appearance: 'error', autoDismiss: true });
            return false;
        }

        const formData = new FormData();
        formData.append('link_url', code);
        axios({
            method: 'post',
            url: 'api/setup-discount-link',
            data: formData,
        }).then((response) => {
            if (response.data.success) {
                setPuChannelName(`private-discounted-subscription-${userId}`);
                setDiscountCode(response.data.link_url);
                setDiscountUrl(`${appUrl}/trial/${response.data.link_url}`);
                addToast('Profile discount link is now available', { appearance: 'success', autoDismiss: true });
            } else {
                addToast('Cannot regenerate new link', { appearance: 'error', autoDismiss: true });
            }

            setIsGenerating(false);
        }).catch((error) => {
            console.log(error);
            setIsGenerating(false);
            addToast('Please create new link', { appearance: 'error', autoDismiss: true });
        });
    };

    const handleCopy = (link) => {
        copy(link, {
            debug: false,
            message: 'Click to copy caption'
        });

        addToast('Discount link successfully copied', { appearance: 'success', autoDismiss: true });
    };

    const updateLinkCreatedAt = (date) => {
        const discountLinkCreatedAt = moment.utc(date).local();

        if (!date || isNaN(discountLinkCreatedAt)) {
            setLinkCreatedAt('-');
        } else {
            const currYear = moment.utc().local().year();

            // check discount link's year created
            if (discountLinkCreatedAt.year() === currYear) {
                setLinkCreatedAt(discountLinkCreatedAt.format('MMM D'));
            } else {
                setLinkCreatedAt(discountLinkCreatedAt.format('MMM D, YYYY'));
            }
        }
    };

    const deleteLink = async () => {
        axios.delete('api/delete-discount-link')
            .then((response) => {
                if (response.data.success) {
                    setDiscountCode('');
                    setDiscountUrl('');
                    formik.setFieldValue('link_name', '', true);
                    formik.setFieldValue('link_price', user.link_price, true);
                    updateLinkCreatedAt(null);
                    setLinkClaimsCount('-');
                    addToast('Discount link is removed', { appearance: 'success', autoDismiss: true });
                } else {
                    addToast('Cannot remove discount link', { appearance: 'error', autoDismiss: true });
                }
                setIsGenerating(false);
            }).catch((error) => {
                console.log(error);
                setIsGenerating(false);
                addToast('Cannot remove discount link', { appearance: 'error', autoDismiss: true });
            });
    };

    useDidMountEffect(() => {
        let isMounted = true;

        // first load of page
        if (user.link_url !== '' && discountCode === '') {
            setDiscountUrl(`${appUrl}/trial/${user.link_url}`);
            setDiscountCode(user.link_url);
            setLinkClaimsCount(user?.claims_count == null ? '-' : user.claims_count);

            if (user.link_created) {
                updateLinkCreatedAt(user.link_created);
            }

            setPuChannelName(`private-discounted-subscription-${userId}`);
        }

        return (() => { isMounted = false; });
    }, [user]);

    const updateClaimsCount = data => {
        if (userId === data.id) {
            setLinkClaimsCount(data.claims_count);
        }
    };

    useEffect(() => {
        if (puChannelName) {
            setPuChannel(pusher.subscribe(puChannelName));
        }
    }, [puChannelName]);

    useEffect(() => {
        if (puChannel) {
            puChannel.bind('App\\Events\\DiscountedSubscriptionEvent', (d) => {
                updateClaimsCount(d);
            });
        }

        return () => {
            pusher.unsubscribe(puChannelName);
        };
    }, [puChannel]);

    const formik = useFormik({
        initialValues: {
            link_price: discountPrice.minAmount,
            link_enabled: enabled,
            link_name: '',
            subscription_price: 0,
        },
        validationSchema: Yup.object({
            link_price: Yup.lazy(value => {
                return Yup.number()
                    .test(
                        'is-decimal',
                        'Invalid discount price',
                        value => (value + "").match(/^[0-9]\d*(\.\d+)?$/)
                        ,)
                    .typeError('Invalid discount price')
                    .moreThan(
                        discountPrice.minAmount,
                        `Enter at least ${currency(discountPrice.minAmount).add(.01).format()}`
                    )
                    .lessThan(user.subscription_price, `Please enter a value less than ${currency(user.subscription_price).format()}`);
            }),
            link_name: Yup.lazy((value) => {
                return Yup.string()
                    .required()
                    .test(
                        'is-valid',
                        'Invalid link name pattern. Only accepts alphanumeric values, dash (-) and underscore (_) without space',
                        value => value
                            ? (value).match(/^[a-z\d\-_]+$/i)
                            : ''
                    )
                    .typeError(`Invalid link name pattern. Only accepts alphanumeric values, dash (-) and underscore (_) without space`)
                    .min(linkNameLength.minLength, `Link name should not be less than ${linkNameLength.minLength} characters`)
                    .max(linkNameLength.maxLength, `Link name should not be more than  ${linkNameLength.maxLength} characters`);
            }),
            subscription_price: Yup.lazy(value => {
                return Yup.number()
                    .test(
                        'should more that minimum subscription price',
                        'Your subscription price is lower than the lowest allowed. Click here to adjust it.',
                        value => value >= minSubscriptionPrice
                    );
            }),
        }),
        onSubmit: (values, { setErrors }) => {
            // console.log('submit form', values);
            generateNewLink();
            submitForm(values);
        },
    });

    const submitForm = async (values) => {
        await axios.patch(`/api/update-discount-settings`, { ...values })
            .then((response) => {
                if (response.data.success) {
                    updateLinkCreatedAt(moment.utc().local());
                    setLinkClaimsCount(0);
                    addToast('Successfully created discount link', { appearance: 'success', autoDismiss: true });
                } else {
                    addToast('Cannot discount settings', { appearance: 'error', autoDismiss: true });
                }
            })
            .catch((error) => {
                console.log({ error });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleDiscountPriceChange = (e) => {
        e.preventDefault();
        let amount = e.target.value ?? user.link_price;

        formik.setFieldValue('link_price', amount, true);
        setAmount(e.target.value);

        let percent = calculatePercentage(e.target.value, user.subscription_price);

        if (isNaN(percent)) {
            setPercentShow(0);
        } else {
            setPercentShow(percent.toFixed(2).replace('.00', ''));
        }
    };

    const validateLinkName = (value) => {
        if (!value.toString().match(/^[a-z\d\-_]+$/i)) {
            formik.setErrors({
                linkName: 'Invalid link name pattern. Only accepts alphanumeric values, dash (-) and underscore (_) without space',
            });
        }

        return value;
    };

    useEffect(() => {
        if (amount > 0) {
            let price = amount.toString().replace(/^0+/, '');
            price = price.replace(/[\.\%]/g, function (match, offset, all) {
                return match === "." ? (all.indexOf(".") === offset ? '.' : '') : '';
            });
            const fixedNum = toFixedTrunc(parseFloat(price), 2);
            formik.setFieldValue('link_price', fixedNum, true);
        }
    }, [amount]);

    useDidMountEffect(() => {
        let isMounted = true;

        setEnabled(user.link_enabled);
        formik.setFieldValue('link_price', user.link_price, true);

        let percent = calculatePercentage(user.link_price, user.subscription_price);

        if (isNaN(percent)) {
            setPercentShow(0);
        } else {
            setPercentShow(percent.toFixed(2).replace('.00', ''));
        }

        formik.setFieldValue('link_enabled', enabled);
        formik.setFieldValue('link_name', user.link_name);
        formik.setFieldValue('subscription_price', user.subscription_price);

        return (() => { isMounted = false; });
    }, [user]);

    const confirmDeleteDiscountLink = () => {
        setDeleteMessage(`<b>Delete the discount link?</b><br />Unsubscribed users will not be able to use this link to subscribe to your account.`);
        setShowDeleteConfirm(true);
    };

    return <div className='discount-section'>
        <form onSubmit={formik.handleSubmit}>
            <div className="promotionpanel-fields">
                <div className="promo__status mb-4" style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '15px' }}>
                    <span>DISCOUNT LINK</span>
                </div>
                <div className="promotion-field-group discount-link-metrics" style={{ display: 'flex', flexDirection: 'row', gap: '50px' }}>
                    <label>
                        <b>Started</b>
                        <span>{linkCreatedAt}</span>
                    </label>
                    <label>
                        <b>Claims Count</b>
                        <span>{linkClaimsCount}</span>
                    </label>
                </div>
                <div className="promotion-field-group">
                    <label><b>Label</b><span className="pt-2">Your label for the new subscriber count.</span></label>
                    <input
                        type='text'
                        name={'link_name'}
                        value={formik.values.link_name ?? ''}
                        className={`discount-link-label form-control${formik.errors.link_name && formik.touched.link_name ? ' is-invalid' : ''}`}
                        onChange={formik.handleChange}
                        error={formik.errors.link_name && formik.touched.link_name}
                    />
                    {formik.errors.link_name && formik.touched.link_name ? <div className="invalid-feedback" style={{ textAlign: 'center' }}>{formik.errors.link_name}</div> : null}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
                    <p className="promo__status">Link Price</p>
                    <div className="promotion-field-input-description">
                        <PriceV2
                            id="link_price"
                            name="link_price"
                            placeholder={user.link_price}
                            className={`ppv-price ${formik.errors.link_price && formik.touched.link_price ? ' is-invalid' : ''}`}
                            onChange={handleDiscountPriceChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.link_price}
                            error={formik.errors.link_price && formik.touched.link_price}
                            inputMode="decimal"
                        />
                    </div>
                    {formik.errors.link_price && formik.touched.link_price ? <div className="invalid-feedback" style={{ textAlign: 'center' }}>{formik.errors.link_price}</div> : null}
                    <p className="pt-2">The discount that people will see on your profile for this link is <span className="highlight" style={{ color: '#0097fe' }}>{percentShow}%</span></p>
                </div>
            </div>

            <div className="profile-form-button-container">
                {
                    !discountUrl
                        ? <div className="profile-form-button promotional-btns create-link-button mb-2" style={{ textAlign: 'center' }}>
                            <button type="submit" className="btn theme-btn theme-btn-bordered"
                                // onClick={generateNewLink}
                                disabled={(formik.values.link_name === '' || formik.values.link_name === null) || (formik.errors.link_name && formik.touched.link_name)}
                            >
                                {isGenerating ? 'Generating' : 'Create New Link'}
                            </button>
                        </div>
                        : <div className="profile-form-button promotional-btns create-link-button discount-link mb-2">
                            <button type="button"
                                className="btn theme-btn theme-btn-bordered"
                                disabled={(discountUrl === '')}
                                onClick={() => handleCopy(discountUrl)}>Copy Link
                            </button>
                            <button type="button"
                                className="btn theme-btn theme-btn-bordered"
                                disabled={(discountUrl === '')}
                                onClick={confirmDeleteDiscountLink}>Delete Link
                            </button>
                            <p className="discount-url">{discountUrl}</p>
                        </div>
                }

                {/* {
                    (formik.errors.subscription_price && formik.touched.subscription_price)
                        ? <div className="invalid-feedback" style={{ textAlign: 'center' }}>Your subscription price is lower than the lowest allowed. Click <a href={'/become-creator'}>here</a> to adjust it.</div>
                        : <>
                            {
                                discountUrl && <div style={{width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                                <p style={{display: 'flex', justifyContent: 'center', overflowWrap: 'anywhere'}}>{discountUrl}</p>
                                <div className="discount-buttons promotional-btns mb-2">
                                    <button type="button" className="btn theme-btn theme-btn-bordered text-capitalize"
                                            disabled={(discountUrl === '')}
                                            onClick={() => handleCopy(discountUrl)}>Copy Link
                                    </button>
                                    <button type="button" className="btn theme-btn theme-btn-bordered text-capitalize"
                                            disabled={(discountUrl === '')}
                                            onClick={deleteLink}>Delete Link
                                    </button>
                                </div>
                            </div>
                            }
                        </>
                } */}
            </div>

            {
                showDeleteConfirm && <AlertConfirmModal
                    show={showDeleteConfirm}
                    setShow={setShowDeleteConfirm}
                    modalBody={deleteMessage}
                    useFunction={deleteLink}
                    isConfirm
                />
            }
        </form >
    </div>;
};

export default DiscountLink;
